import {
  Directive,
  EmbeddedViewRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  ɵstringify as stringify,
} from '@angular/core';
import { AuthSelectors, RootState } from '@app/store/reducers';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appIfHasPrivileges]',
  standalone: true,
})
export class IfHasPrivilegesDirective<T = unknown> implements OnInit, OnDestroy {
  private _context = new AppIfHasPrivilegesContext<T>();
  private readonly _thenTemplateRef: TemplateRef<AppIfHasPrivilegesContext<T>> | null = null;
  private _elseTemplateRef: TemplateRef<AppIfHasPrivilegesContext<T>> | null = null;
  private _thenViewRef: EmbeddedViewRef<AppIfHasPrivilegesContext<T>> | null = null;
  private _elseViewRef: EmbeddedViewRef<AppIfHasPrivilegesContext<T>> | null = null;


  currentUserPrivileges: string[] = [];
  requiredPrivileges: string[];

  destroyed$ = new Subject<void>();

  constructor(
    private store: Store<RootState>,
    private _viewContainer: ViewContainerRef,
    templateRef: TemplateRef<AppIfHasPrivilegesContext<T>>,
  ) {
    this._thenTemplateRef = templateRef;
  }

  /**
   * The current user must have all these privileges for the element to render
   *
   * @param privileges
   */
  @Input()
  set appIfHasPrivileges(privileges: string[]) {
    this.requiredPrivileges = privileges;
    this._updateView();
  }

  @Input()
  set appIfHasPrivilegesElse(templateRef: TemplateRef<AppIfHasPrivilegesContext<T>>|null) {
    assertTemplate('appIfHasPrivilegeElse', templateRef);
    this._elseTemplateRef = templateRef;
    this._elseViewRef = null;  // clear previous view if any.
    this._updateView();
  }

  ngOnInit() {
    this.store
      .pipe(
        select(AuthSelectors.currentUserPrivileges),
        takeUntil(this.destroyed$),
        filter(currentUserPrivileges => !!currentUserPrivileges),
      )
      .subscribe((currentUserPrivileges) => {
        this.currentUserPrivileges = currentUserPrivileges;
        this._updateView();
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }

  private _updateView() {
    if (this.checkPermission()) {
      if (!this._thenViewRef) {
        this._viewContainer.clear();
        this._elseViewRef = null;
        if (this._thenTemplateRef) {
          this._thenViewRef =
            this._viewContainer.createEmbeddedView(this._thenTemplateRef, this._context);
        }

      }
    } else {
      if (!this._elseViewRef) {
        this._viewContainer.clear();
        this._thenViewRef = null;
        if (this._elseTemplateRef) {
          this._elseViewRef =
            this._viewContainer.createEmbeddedView(this._elseTemplateRef, this._context);
        }
      }
    }
  }

  private checkPermission() {
    let missing = false;

    if (this.requiredPrivileges) {
      if (!this.currentUserPrivileges) {
        return false;
      }

      for (const privilege of this.requiredPrivileges) {
        if (!this.currentUserPrivileges.includes(privilege)) {
          missing = true;
          break;
        }
      }
    }

    return !missing;
  }
}

export class AppIfHasPrivilegesContext<T = unknown> {
  public appIfHasPrivileges: T = null;
}

function assertTemplate(property: string, templateRef: TemplateRef<any>|null): void {
  const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
  if (!isTemplateRefOrNull) {
    throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
  }
}
